/**
 * Timezone-aware display helpers for event/market/score times.
 *
 * Times stored as absolute instants (ISO-8601 strings, or unix-ms via Date)
 * are rendered in an operator-chosen *display* timezone so support staff read
 * them consistently regardless of the machine clock. All formatting uses the
 * native `Intl` API, so no extra dependency is required.
 */

export interface TimezoneOption {
  label: string;
  value: string;
}

/** Empty value = fall back to the browser's local timezone. */
export const LOCAL_TZ_VALUE = '';

/**
 * Zones never shown in the picker. We exclude the Russian Federation zones
 * (Metagaim/Betbazar/B1 is a Ukrainian company — avoid Russia-related entries).
 * Simferopol is included here: it is Ukrainian Crimea but the IANA zone tracks
 * Russian (Moscow) time under occupation, so it is omitted too.
 */
const EXCLUDED_ZONES = new Set<string>([
  'Europe/Kaliningrad', 'Europe/Moscow', 'Europe/Simferopol', 'Europe/Kirov',
  'Europe/Volgograd', 'Europe/Astrakhan', 'Europe/Saratov', 'Europe/Ulyanovsk',
  'Europe/Samara', 'Asia/Yekaterinburg', 'Asia/Omsk', 'Asia/Novosibirsk',
  'Asia/Barnaul', 'Asia/Tomsk', 'Asia/Novokuznetsk', 'Asia/Krasnoyarsk',
  'Asia/Irkutsk', 'Asia/Chita', 'Asia/Yakutsk', 'Asia/Khandyga',
  'Asia/Vladivostok', 'Asia/Ust-Nera', 'Asia/Magadan', 'Asia/Sakhalin',
  'Asia/Srednekolymsk', 'Asia/Kamchatka', 'Asia/Anadyr',
]);

/** Frequently-used shortcuts surfaced at the top of the picker. */
export const COMMON_TIMEZONES: TimezoneOption[] = [
  { label: 'Local (your computer)', value: LOCAL_TZ_VALUE },
  { label: 'UTC', value: 'UTC' },
  { label: 'CET — Central Europe', value: 'CET' },
  { label: 'EET — Eastern Europe', value: 'EET' },
  { label: 'Kyiv', value: 'Europe/Kyiv' },
  { label: 'London', value: 'Europe/London' },
  { label: 'Lisbon', value: 'Europe/Lisbon' },
  { label: 'IST — India', value: 'Asia/Kolkata' },
  { label: 'Gulf — Dubai', value: 'Asia/Dubai' },
  { label: 'New York', value: 'America/New_York' },
];

type TimeValue = string | number | Date;

const toDate = (value: TimeValue): Date => (value instanceof Date ? value : new Date(value));

/** An empty timeZone string means "use the runtime's local zone". */
const resolveZone = (tz: string): string | undefined => (tz ? tz : undefined);

/** Format an instant as HH:mm (24h) in the chosen timezone. */
export const formatTimeInTz = (value: TimeValue, tz: string): string => {
  if (!value) return '';
  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: resolveZone(tz),
  }).format(toDate(value));
};

/** Format an instant as HH:mm:ss (24h) in the chosen timezone. */
export const formatTimeWithSecondsInTz = (value: TimeValue, tz: string): string => {
  if (!value) return '';
  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: resolveZone(tz),
  }).format(toDate(value));
};

/**
 * Calendar date (YYYY-MM-DD) of an instant *in the chosen timezone*.
 * Used to group events into day buckets so a match near midnight lands on the
 * correct day for the operator's selected zone. `en-CA` yields ISO ordering.
 */
export const dateKeyInTz = (value: TimeValue, tz: string): string => {
  if (!value) return '';
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: resolveZone(tz),
  }).format(toDate(value));
};

/** Date + time, compact: "YYYY-MM-DD HH:mm" in the chosen timezone. */
export const formatDateTimeInTz = (value: TimeValue, tz: string): string => {
  if (!value) return '';
  return `${dateKeyInTz(value, tz)} ${formatTimeInTz(value, tz)}`;
};

/** Date + time, long: "MMM d, yyyy HH:mm:ss" in the chosen timezone. */
export const formatDateTimeLongInTz = (value: TimeValue, tz: string): string => {
  if (!value) return '';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: resolveZone(tz),
  }).format(toDate(value));
};

/** Short zone label (e.g. "GMT+5:30", "UTC", "local") for column headers. */
export const tzShortLabel = (tz: string): string => {
  if (!tz) return 'local';
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZoneName: 'short',
    timeZone: tz,
  }).formatToParts(new Date());
  return parts.find((p) => p.type === 'timeZoneName')?.value ?? tz;
};

/** Current UTC offset label for a zone, e.g. "UTC+02:00". Empty on failure. */
export const zoneOffsetLabel = (zone: string): string => {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: zone,
      timeZoneName: 'longOffset',
    } as Intl.DateTimeFormatOptions).formatToParts(new Date());
    const raw = parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
    return raw.replace('GMT', 'UTC') || 'UTC';
  } catch {
    return '';
  }
};

/** Minimal cross-region fallback if Intl.supportedValuesOf is unavailable. */
const FALLBACK_ZONES: string[] = [
  'UTC', 'CET', 'EET', 'WET',
  'Europe/Kyiv', 'Europe/London', 'Europe/Lisbon', 'Europe/Madrid', 'Europe/Berlin',
  'Europe/Paris', 'Europe/Warsaw', 'Europe/Istanbul', 'Europe/Athens',
  'Africa/Cairo', 'Africa/Lagos', 'Africa/Johannesburg', 'Africa/Nairobi',
  'Asia/Dubai', 'Asia/Kolkata', 'Asia/Karachi', 'Asia/Dhaka', 'Asia/Bangkok',
  'Asia/Singapore', 'Asia/Hong_Kong', 'Asia/Shanghai', 'Asia/Tokyo', 'Asia/Seoul',
  'Australia/Sydney', 'Australia/Perth', 'Pacific/Auckland',
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Sao_Paulo', 'America/Mexico_City', 'America/Argentina/Buenos_Aires',
];

/** Full IANA zone list (Moscow excluded), with a fallback for old runtimes. */
export const getAllTimeZones = (): string[] => {
  const sof = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf;
  const zones = typeof sof === 'function' ? sof('timeZone') : FALLBACK_ZONES;
  return zones.filter((z) => !EXCLUDED_ZONES.has(z));
};

export interface TimezoneGroup {
  region: string;
  options: TimezoneOption[];
}

/** All zones grouped by region (Africa, America, Asia, Europe, ...) for a
 *  compact native <select> with <optgroup>s. Labels include the live offset. */
export const getGroupedTimeZones = (): TimezoneGroup[] => {
  const byRegion = new Map<string, TimezoneOption[]>();
  for (const zone of getAllTimeZones()) {
    const slash = zone.indexOf('/');
    const region = slash === -1 ? 'Other' : zone.slice(0, slash);
    const city = slash === -1 ? zone : zone.slice(slash + 1).replace(/_/g, ' ');
    const offset = zoneOffsetLabel(zone);
    const label = offset ? `${city} (${offset})` : city;
    const list = byRegion.get(region) ?? [];
    list.push({ label, value: zone });
    byRegion.set(region, list);
  }
  return Array.from(byRegion.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([region, options]) => ({
      region,
      options: options.sort((a, b) => a.label.localeCompare(b.label)),
    }));
};
